import React, { useState, useRef, useEffect } from 'react';
import { auth, db } from '../firebase';
import {
  RecaptchaVerifier, signInWithPhoneNumber,
} from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';

const S = {
  page: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  logo: { fontSize: 48, marginBottom: 8 },
  title: { fontSize: 28, fontWeight: 900, color: 'var(--text)', marginBottom: 6 },
  sub: { fontSize: 15, color: 'var(--subtext)', marginBottom: 40, textAlign: 'center' },
  card: {
    width: '100%', maxWidth: 400,
    background: 'var(--card)', borderRadius: 20,
    border: '1px solid var(--border)', padding: 28,
  },
  label: { fontSize: 13, fontWeight: 600, color: 'var(--subtext)', marginBottom: 8, display: 'block' },
  input: {
    width: '100%', background: 'var(--input-bg)', border: '1px solid var(--border)',
    borderRadius: 12, padding: '14px 16px', fontSize: 16, color: 'var(--text)',
    outline: 'none', marginBottom: 16, boxSizing: 'border-box',
  },
  row: { display: 'flex', gap: 12 },
  btn: {
    width: '100%', background: 'var(--accent)', borderRadius: 14,
    padding: '16px', fontSize: 17, fontWeight: 900, color: '#000',
    marginTop: 4, border: 'none', cursor: 'pointer',
  },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  hint: { fontSize: 13, color: 'var(--subtext)', textAlign: 'center', marginTop: 16, lineHeight: 1.5 },
  codeRow: { display: 'flex', gap: 8, marginBottom: 16 },
  codeInput: {
    flex: 1, background: 'var(--input-bg)', border: '2px solid var(--border)',
    borderRadius: 12, padding: '18px 8px', fontSize: 24, fontWeight: 800,
    color: 'var(--text)', textAlign: 'center', outline: 'none', width: 0,
  },
  back: {
    background: 'none', color: 'var(--subtext)', fontSize: 15,
    padding: '12px 0', marginTop: 8, width: '100%', border: 'none', cursor: 'pointer',
  },
  error: { color: 'var(--danger)', fontSize: 13, marginBottom: 12 },
};

export default function Login() {
  const [step,        setStep]        = useState('phone'); // phone | code | profile
  const [phone,       setPhone]       = useState('');
  const [code,        setCode]        = useState(['', '', '', '', '', '']);
  const [firstName,   setFirstName]   = useState('');
  const [lastName,    setLastName]    = useState('');
  const [email,       setEmail]       = useState('');
  const [confirm,     setConfirm]     = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const codeRefs = useRef([]);
  const recaptchaRef = useRef(null);

  useEffect(() => {
    if (!window.recaptchaVerifier) {
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible',
      });
    }
  }, []);

  async function sendCode() {
    setError('');
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 10) { setError('Enter a valid phone number'); return; }
    const formatted = cleaned.startsWith('1') ? `+${cleaned}` : `+1${cleaned}`;
    setLoading(true);
    try {
      const result = await signInWithPhoneNumber(auth, formatted, window.recaptchaVerifier);
      setConfirm(result);
      setStep('code');
    } catch (e) {
      setError('Could not send code. Try again.');
      console.error(e);
      window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
    } finally {
      setLoading(false);
    }
  }

  function handleCodeInput(val, i) {
    const newCode = [...code];
    newCode[i] = val.slice(-1);
    setCode(newCode);
    if (val && i < 5) codeRefs.current[i + 1]?.focus();
    if (!val && i > 0) codeRefs.current[i - 1]?.focus();
  }

  async function verifyCode() {
    const full = code.join('');
    if (full.length !== 6) { setError('Enter all 6 digits'); return; }
    setLoading(true);
    setError('');
    try {
      await confirm.confirm(full);
      // Check if they already have a name saved locally
      const saved = localStorage.getItem('ericgcuts_name');
      if (!saved) {
        setStep('profile');
      }
      // If they have a name, onAuthStateChanged in App.js will redirect
    } catch (e) {
      setError('Wrong code. Try again.');
    } finally {
      setLoading(false);
    }
  }

  async function saveProfile() {
    if (!firstName.trim()) { setError('Enter your first name'); return; }
    setLoading(true);
    setError('');
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const cleanedPhone = phone.replace(/\D/g, '');
      const phoneDigits = cleanedPhone.startsWith('1') ? cleanedPhone.slice(1) : cleanedPhone;

      // Write client record to Firestore — marks them as having the customer app
      await setDoc(doc(db, 'clients', phoneDigits), {
        id:               phoneDigits,
        name:             fullName,
        firstName:        firstName.trim(),
        lastName:         lastName.trim(),
        email:            email.trim(),
        phone:            phone.replace(/\D/g, '').slice(-10), // last 10 digits
        hasCustomerApp:   true,
        optInCancellations: false, // opt-in is off by default
        createdAt:        serverTimestamp(),
        updatedAt:        serverTimestamp(),
      }, { merge: true });

      // Save name locally for quick access
      localStorage.setItem('ericgcuts_name', fullName);
      localStorage.setItem('ericgcuts_firstName', firstName.trim());
      localStorage.setItem('ericgcuts_phone', phoneDigits);

      // Auth state change will re-render App and show the main app
      window.location.reload();
    } catch (e) {
      console.error(e);
      setError('Could not save your info. Try again.');
    } finally {
      setLoading(false);
    }
  }

  // ── Step: Phone ──────────────────────────────────────────────────────────
  if (step === 'phone') return (
    <div style={S.page}>
      <div style={S.logo}>✂️</div>
      <div style={S.title}>Eric G Cuts</div>
      <div style={S.sub}>Sign in to book your next cut</div>

      <div style={S.card}>
        <label style={S.label}>Your Phone Number</label>
        <input
          style={S.input}
          type="tel"
          placeholder="(818) 555-1234"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && sendCode()}
          autoFocus
        />
        {error && <div style={S.error}>{error}</div>}
        <button
          style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }}
          onClick={sendCode}
          disabled={loading}
        >
          {loading ? 'Sending…' : 'Send Code →'}
        </button>
        <div style={S.hint}>
          We'll text you a 6-digit code to verify your number. No password needed.
        </div>
      </div>
      <div id="recaptcha-container" />
    </div>
  );

  // ── Step: Code ───────────────────────────────────────────────────────────
  if (step === 'code') return (
    <div style={S.page}>
      <div style={S.logo}>✂️</div>
      <div style={S.title}>Enter Code</div>
      <div style={S.sub}>We texted a 6-digit code to {phone}</div>

      <div style={S.card}>
        <div style={S.codeRow}>
          {code.map((d, i) => (
            <input
              key={i}
              ref={el => codeRefs.current[i] = el}
              style={S.codeInput}
              type="tel"
              maxLength={1}
              value={d}
              onChange={e => handleCodeInput(e.target.value, i)}
              onKeyDown={e => {
                if (e.key === 'Backspace' && !code[i] && i > 0) {
                  codeRefs.current[i - 1]?.focus();
                }
              }}
              autoFocus={i === 0}
            />
          ))}
        </div>
        {error && <div style={S.error}>{error}</div>}
        <button
          style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }}
          onClick={verifyCode}
          disabled={loading}
        >
          {loading ? 'Verifying…' : 'Verify →'}
        </button>
        <button style={S.back} onClick={() => { setStep('phone'); setCode(['','','','','','']); setError(''); }}>
          ← Change number
        </button>
      </div>
      <div id="recaptcha-container" />
    </div>
  );

  // ── Step: Profile ─────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.logo}>✂️</div>
      <div style={S.title}>Tell us about you</div>
      <div style={S.sub}>So Eric knows who's booking</div>

      <div style={S.card}>
        <div style={S.row}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>First Name *</label>
            <input
              style={{ ...S.input }}
              type="text"
              placeholder="Alex"
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              autoFocus
              autoCapitalize="words"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Last Name</label>
            <input
              style={{ ...S.input }}
              type="text"
              placeholder="Smith"
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              autoCapitalize="words"
            />
          </div>
        </div>

        <label style={S.label}>Email (for receipts & reminders)</label>
        <input
          style={S.input}
          type="email"
          placeholder="alex@example.com"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && saveProfile()}
          autoCapitalize="none"
        />

        {error && <div style={S.error}>{error}</div>}
        <button
          style={{ ...S.btn, ...(loading ? S.btnDisabled : {}) }}
          onClick={saveProfile}
          disabled={loading}
        >
          {loading ? 'Saving…' : "Let's Go →"}
        </button>
        <div style={S.hint}>Your info is only shared with Eric's barbershop.</div>
      </div>
    </div>
  );
}
